import {
  Alert,
  AlertIcon,
  Box,
  Divider,
  HStack,
  IconButton,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import Layout from "components/Layout";
import { useConfirmation } from "components/hooks/useConfirmation";
import useUser from "components/hooks/useUser";
import { MdArrowRightAlt } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import RequestTable from "components/RequestTable";
import { useMemo } from "react";
import { useEntities } from "components/hooks/useEntities";
import { Item, WithId } from "types";

const LOW_STOCK_FIELDS = ["*", "inventory_items.*"];
const LOW_STOCK_DEEP = { inventory_items: { _limit: 1, _sort: "-created_at" } };

export default function Home() {
  const navigate = useNavigate();
  const user = useUser();
  const sectionBg = useColorModeValue("blackAlpha.200", "whiteAlpha.200");
  const lockStockActionColor = useColorModeValue("black", "white");

  const { node: Confirmation } = useConfirmation();

  const pendingRequestFilters = useMemo(
    () => ({
      approval_status: { _eq: "pending" },
      requested_by: { _neq: user.id },
    }),
    [user.id]
  );

  const { list: lowStockItems } = useEntities<WithId<Item>>({
    entity: "Item",
    fields: LOW_STOCK_FIELDS,
    deep: LOW_STOCK_DEEP,
    paginate: false,
  });

  const alertsList = lowStockItems.filter(
    (item) =>
      (item?.inventory_items?.[0]?.balance ?? 0) <= (item.reorder_level ?? 0)
  );

  return (
    <Layout>
      <Box>
        <HStack justifyContent={"space-between"} w={"100%"}>
          <Text fontSize="md">Hello 👋🏾 {user.first_name},</Text>
        </HStack>
        <Divider my={2} />
      </Box>

      <Stack height={"100%"}>
        <Stack
          spacing={4}
          direction={["column", "column", "row"]}
          height={"100%"}
        >
          <Stack w={["100%", "100%", "70%"]} height={"100%"}>
            <Text fontSize="xl">Pending Requests</Text>

            <RequestTable filter={pendingRequestFilters} showReview />
          </Stack>

          <Stack w={["100%", "100%", "30%"]} height={"100%"}>
            <Text fontSize="xl">Low Stock Items</Text>

            <VStack
              background={sectionBg}
              alignItems={"center"}
              justifyContent={alertsList.length === 0 ? "center" : undefined}
              height={"100%"}
              p={2}
              spacing={2}
            >
              {alertsList.length === 0 && (
                <Text fontSize="sm">
                  No items are low in stock. You are doing great! 🎉
                </Text>
              )}
              {alertsList.length > 0 &&
                alertsList.map((item) => (
                  <Alert
                    key={item.id}
                    status="warning"
                    variant="left-accent"
                    rounded={"md"}
                  >
                    <AlertIcon />
                    <Text flex={1}>
                      {item.name} is low in stock. Only{" "}
                      {item?.inventory_items?.[0]?.balance ?? 0} left
                    </Text>
                    <IconButton
                      onClick={() => navigate(`/items/${item.id}`)}
                      icon={<MdArrowRightAlt />}
                      aria-label="View Item"
                      color={lockStockActionColor}
                      variant="link"
                    />
                  </Alert>
                ))}
            </VStack>
          </Stack>
        </Stack>
      </Stack>
      {Confirmation}
    </Layout>
  );
}
